import { ComponentColor } from "@common/types";
import { OrganizationStatus } from "@models/organization";

// Human label + daisyUI badge color for each organization status. There is no
// org approval (§1.2): None means "active", and the only real states are
// Suspended and Cancelled.
export const STATUS_META: Record<
    OrganizationStatus,
    { label: string; color: ComponentColor }
> = {
    [OrganizationStatus.None]: { label: "Active", color: ComponentColor.Success },
    [OrganizationStatus.Suspended]: {
        label: "Suspended",
        color: ComponentColor.Warning,
    },
    [OrganizationStatus.Cancelled]: {
        label: "Cancelled",
        color: ComponentColor.Error,
    },
};

export interface StatusAction {
    key: "suspend" | "reactivate" | "cancel";
    label: string;
    // The confirmation dialog's two buttons, defaulting to `label` and
    // "Go back". They must never share a label: a back-out reading "Cancel"
    // beside a confirm reading "Cancel" made Enter cancel the client
    // permanently (#452 / AO-09). No button reads "Cancel" unless it cancels
    // the client.
    confirmLabel?: string;
    dismissLabel?: string;
    // AO-09: cancelling requires a reason; every other action takes one
    // optionally. Either way it is stored in the client's status history.
    // Set only on an action that requires one: the sentence shown, and read
    // out for the disabled confirm, while the reason is empty.
    missingReasonMessage?: string;
    // A line the dialog adds after naming the target status.
    note?: string;
    target: OrganizationStatus;
    color: ComponentColor;
}

// What reactivating does NOT do (#460): the status change alone charges
// nothing and sends no email. A membership that lapsed meanwhile is charged
// later by the billing job or the customer's card save, hence "by itself".
const REACTIVATION_SIDE_EFFECTS =
    "Reactivating doesn't charge or email the client by itself.";

const SUSPEND: StatusAction = {
    key: "suspend",
    label: "Suspend",
    target: OrganizationStatus.Suspended,
    color: ComponentColor.Warning,
};

// Reactivate returns the org to "active", which is the absence of a status (None).
const REACTIVATE: StatusAction = {
    key: "reactivate",
    label: "Reactivate",
    note: REACTIVATION_SIDE_EFFECTS,
    target: OrganizationStatus.None,
    color: ComponentColor.Success,
};

const CANCEL: StatusAction = {
    key: "cancel",
    label: "Cancel",
    confirmLabel: "Cancel client",
    dismissLabel: "Keep client",
    missingReasonMessage: "Enter a reason to cancel this client.",
    note: `You can reactivate the client later. ${REACTIVATION_SIDE_EFFECTS}`,
    target: OrganizationStatus.Cancelled,
    color: ComponentColor.Error,
};

// Mirrors the backend transition table (UpdateOrganizationStatusHandler):
// None -> Suspended | Cancelled; Suspended -> None | Cancelled;
// Cancelled -> None only (AO-09, #460: a cancelled client can be reactivated).
export const ALLOWED_ACTIONS: Record<OrganizationStatus, StatusAction[]> = {
    [OrganizationStatus.None]: [SUSPEND, CANCEL],
    [OrganizationStatus.Suspended]: [REACTIVATE, CANCEL],
    [OrganizationStatus.Cancelled]: [REACTIVATE],
};
